/**
 * LeaderboardCard — Top 3 podium for the most recent exam.
 * Trophy + medal podium with victory animations.
 *   • Desktop: hover any winner → tooltip with subject-wise marks.
 *   • Mobile: tap the ⓘ button → bottom-sheet with subject-wise marks.
 *   • A big "Your performance" banner below the podium shows the current rank,
 *     trend (↑/↓/=) and a short motivational quote.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PresenceDot from "@/components/PresenceDot";
import StudentAnswerSheetViewer from "@/components/StudentAnswerSheetViewer";
import { Trophy, Medal, Award, Sparkles, TrendingUp, TrendingDown, Minus, Info, X, ChevronDown, ChevronUp, ListOrdered, Flame, ChevronLeft, ChevronRight, Crown, FileText } from "lucide-react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const PODIUM_HEIGHTS = { 1: "h-24", 2: "h-20", 3: "h-16" };
const PODIUM_TONE = {
  1: {
    ring: "ring-amber-300 dark:ring-amber-500/60",
    bar: "bg-gradient-to-b from-amber-300 via-amber-400 to-amber-500",
    text: "text-amber-600 dark:text-amber-300",
    icon: Trophy,
    glow: "shadow-[0_0_30px_rgba(251,191,36,0.45)]",
  },
  2: {
    ring: "ring-slate-300 dark:ring-slate-400/60",
    bar: "bg-gradient-to-b from-slate-200 via-slate-300 to-slate-400",
    text: "text-slate-600 dark:text-slate-200",
    icon: Medal,
    glow: "shadow-[0_0_20px_rgba(148,163,184,0.45)]",
  },
  3: {
    ring: "ring-orange-300 dark:ring-orange-500/60",
    bar: "bg-gradient-to-b from-orange-300 via-orange-400 to-orange-500",
    text: "text-orange-600 dark:text-orange-300",
    icon: Medal,
    glow: "shadow-[0_0_20px_rgba(251,146,60,0.4)]",
  },
};

// 4–5 word quotes — picked deterministically by hash(delta + rank) so the same
// student doesn't see a different quote on every reload.
const UP_QUOTES = [
  "Brilliant climb — keep going!",
  "You're on fire today!",
  "Hard work pays off!",
  "Soaring higher every test!",
  "Champion move — well done!",
];
const DOWN_QUOTES = [
  "Tough day — you've got this.",
  "Fall down, rise stronger!",
  "Every champion stumbles sometimes.",
  "Bounce back, you can!",
  "One step back, two ahead!",
];
const SAME_QUOTES = [
  "Steady wins the race.",
  "Consistency is your superpower!",
  "Keep at it — you're growing!",
  "Stable run — push for more!",
];
const TOPPER_QUOTES = [
  "Crown holder — keep shining!",
  "Top of the class — legendary!",
  "First place — unstoppable you!",
  "Reign on, brilliant mind!",
];
const NEUTRAL_QUOTES = [
  "First exam logged — go strong!",
  "Your journey starts here!",
  "Set the benchmark high!",
  "Make every test count!",
];

const initials = (name = "") =>
  (name.trim().split(/\s+/).map((p) => p[0]).join("").slice(0, 2) || "?").toUpperCase();

export default function LeaderboardCard({ endpoint = "/student/leaderboard", params = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mobile tap-to-flip — which student's bottom sheet is open?
  const [openStudentId, setOpenStudentId] = useState(null);
  // Which student's answer sheets are being viewed (photo click).
  const [sheetView, setSheetView] = useState(null);
  // student_id -> { status, last_seen_at } so we can paint presence dots on
  // the podium avatars and the full ranking list. Refreshed every 30s.
  const [presence, setPresence] = useState({});

  const paramKey = params ? JSON.stringify(params) : "";
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(endpoint, params ? { params } : undefined);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setData({ available: false });
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, paramKey]);

  // Once we know the class+section, poll presence for those classmates.
  // Presence is a student-only endpoint, so skip it for other views (teacher).
  useEffect(() => {
    if (endpoint !== "/student/leaderboard") return;
    if (!data?.class_name || !data?.section) return;
    let cancelled = false;
    let timer = null;
    const fetchPresence = async () => {
      try {
        const { data: p } = await api.get("/student-presence", {
          params: { class_name: data.class_name, section: data.section },
        });
        if (!cancelled) setPresence(p || {});
      } catch {/* silent — presence dots simply stay offline */}
    };
    fetchPresence();
    timer = setInterval(fetchPresence, 30000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [data?.class_name, data?.section]);

  if (loading) {
    return (
      <div className="rounded-3xl border border-slate-200/60 dark:border-slate-700/60 bg-gradient-to-br from-amber-50/40 via-rose-50/30 to-violet-50/40 dark:from-slate-800/40 dark:via-slate-800/50 dark:to-slate-900/40 p-5 mb-6 animate-pulse" data-testid="leaderboard-card-loading">
        <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
        <div className="h-24 bg-slate-200/60 dark:bg-slate-700/60 rounded" />
      </div>
    );
  }
  if (!data?.available) return null;

  // All students within the top 3 rank tiers (dense ranking). Ties mean a tier
  // can hold multiple students — each gets its own podium step. Ordered left to
  // right by rank (rank 1 first), so a tie reads 1,1,2,2,3.
  const podiumStudents = data.podium || data.top3 || [];
  const byRank = { 1: [], 2: [], 3: [] };
  podiumStudents.forEach((s) => { if (s && byRank[s.rank]) byRank[s.rank].push(s); });
  [1, 2, 3].forEach((r) => byRank[r].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
  const podium = [...byRank[1], ...byRank[2], ...byRank[3]];
  const me = data.me;
  const meInTop3 = me && podiumStudents.some((t) => t?.student_id === me.student_id);
  const openStudent = openStudentId ? podiumStudents.find((t) => t?.student_id === openStudentId) : null;

  return (
    <div
      className="relative overflow-visible rounded-3xl border border-amber-200/40 dark:border-amber-700/30 bg-gradient-to-br from-amber-50 via-rose-50 to-violet-50 dark:from-amber-950/40 dark:via-rose-950/30 dark:to-violet-950/40 p-5 sm:p-6 mb-6"
      data-testid="leaderboard-card"
    >
      <Sparkles className="absolute top-4 right-6 h-5 w-5 text-amber-400/50 animate-pulse" />
      <Sparkles className="absolute bottom-6 left-8 h-4 w-4 text-rose-400/50 animate-pulse [animation-delay:300ms]" />

      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-600 dark:text-amber-300 font-semibold inline-flex items-center gap-1.5">
            <Trophy className="h-3 w-3" /> Leaderboard
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 mt-0.5" style={{ fontFamily: "Outfit" }}>
            Top performers · {data.exam_label}
          </h3>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Class {data.class_name} · Section {data.section} · {data.subjects?.length || 0} subjects · {data.total_ranked} ranked
          </div>
        </div>
      </div>

      {/* Podium */}
      <div className="flex flex-wrap items-end justify-center gap-3 sm:gap-6">
        {podium.map((s) => {
          if (!s) return null;
          const rank = s.rank;
          const tone = PODIUM_TONE[rank];
          const Icon = tone.icon;
          return (
            <div
              key={s.student_id}
              className="group relative flex flex-col items-center"
              data-testid={`leaderboard-rank-${rank}`}
              data-student={s.student_id}
            >
              <div className={`flex items-center gap-1 mb-1.5 text-[11px] font-bold uppercase tracking-wider ${tone.text}`}>
                <Icon className={`h-3.5 w-3.5 ${rank === 1 ? "animate-bounce" : ""}`} />
                #{rank}
              </div>

              <div className={`relative ${rank === 1 ? "animate-[pulse_2.5s_ease-in-out_infinite]" : ""}`}>
                {rank === 1 && (
                  <div className="absolute -inset-1 rounded-full bg-amber-400/30 blur-md animate-ping" />
                )}
                <button
                  type="button"
                  onClick={() => setSheetView(s)}
                  title="View answer sheets"
                  data-testid={`leaderboard-photo-${rank}`}
                  className={`relative h-16 w-16 sm:h-20 sm:w-20 rounded-full ring-4 ${tone.ring} ${tone.glow} overflow-hidden bg-white dark:bg-slate-700 flex items-center justify-center transition-transform group-hover:scale-105 cursor-pointer`}
                >
                  {s.has_photo ? (
                    <img src={`${API_BASE}/profile/photo/${s.student_id}`} alt={s.name} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-lg font-bold text-slate-700 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>{initials(s.name)}</span>
                  )}
                </button>
                {/* Presence dot — bottom-right corner of the avatar */}
                <span className="absolute bottom-0 right-0 inline-flex p-0.5 rounded-full bg-white dark:bg-slate-800 shadow" data-testid={`leaderboard-presence-${rank}`}>
                  <PresenceDot
                    size="md"
                    status={presence[s.student_id]?.status || "offline"}
                    lastSeenAt={presence[s.student_id]?.last_seen_at}
                  />
                </span>
                {/* Mobile-only "ⓘ" tap target — visible only on small screens */}
                <button
                  type="button"
                  onClick={() => setOpenStudentId(s.student_id)}
                  className="sm:hidden absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-slate-900 text-white shadow-lg flex items-center justify-center"
                  aria-label="See subject marks"
                  data-testid={`leaderboard-info-${rank}`}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
                {/* Nudge — this topper has answer sheets to learn from */}
                {s.has_answer_sheets && (
                  <span
                    className="absolute -top-1 -left-1 inline-flex items-center gap-0.5 px-1.5 h-5 rounded-full bg-emerald-500 text-white text-[9px] font-bold shadow-md ring-2 ring-white dark:ring-slate-800 animate-[pulse_2s_ease-in-out_infinite]"
                    title="Answer sheets available — tap the photo"
                    data-testid={`leaderboard-answersheet-badge-${rank}`}
                  >
                    <FileText className="h-2.5 w-2.5" /> Answers
                  </span>
                )}
              </div>

              <div className="mt-2 text-center max-w-[110px] sm:max-w-[140px]">
                <div className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-100 truncate" title={s.name}>{s.name}</div>
                <div className={`text-base sm:text-lg font-bold ${tone.text}`} style={{ fontFamily: "Outfit" }}>{s.percentage}%</div>
                {s.has_answer_sheets && (
                  <button
                    type="button"
                    onClick={() => setSheetView(s)}
                    className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
                    data-testid={`leaderboard-view-answers-${rank}`}
                  >
                    <FileText className="h-3 w-3" /> View answer sheets
                  </button>
                )}
              </div>

              <div className={`w-16 sm:w-20 ${PODIUM_HEIGHTS[rank]} rounded-t-lg ${tone.bar} mt-1 flex items-center justify-center text-white font-bold shadow-md`}>
                <span className="text-xs sm:text-sm" style={{ fontFamily: "Outfit" }}>{rank}</span>
              </div>

              {/* Desktop-only hover tooltip — hidden on small screens */}
              <div
                className="hidden sm:block invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none absolute bottom-full mb-3 left-1/2 -translate-x-1/2 z-20 w-52 sm:w-56 p-3 rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-2xl"
                data-testid={`leaderboard-tooltip-${rank}`}
              >
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1.5">Marks · {data.exam_label}</div>
                <div className="space-y-1">
                  {Object.entries(s.marks_by_subject || {}).map(([sub, m]) => (
                    <div key={sub} className="flex items-center justify-between text-xs">
                      <span className="truncate mr-2 opacity-90">{sub}</span>
                      <span className="font-mono font-semibold">{m.marks}<span className="opacity-50">/{m.max_marks}</span></span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-white/20 dark:border-slate-900/20 flex items-center justify-between text-xs">
                  <span className="opacity-70">Total</span>
                  <span className="font-mono font-bold">{s.total}<span className="opacity-50">/{s.max_total}</span></span>
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px w-3 h-3 bg-slate-900 dark:bg-slate-100 rotate-45" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Your performance — big rank + trend + motivational quote */}
      {me && <YourPerformance me={me} trend={data.trend} meInTop3={meInTop3} streak={data.me_streak} />}

      {/* Subject toppers — auto-rotating carousel */}
      {data.subject_toppers && data.subject_toppers.length > 0 && (
        <SubjectToppersCarousel toppers={data.subject_toppers} examLabel={data.exam_label} onViewSheets={setSheetView} />
      )}

      {/* Full class ranking — collapsible */}
      {data.ranking && data.ranking.length > 3 && (
        <FullRanking ranking={data.ranking} myId={me?.student_id} presence={presence} />
      )}

      <div className="mt-3 pt-3 border-t border-amber-200/40 dark:border-amber-700/30 text-[11px] text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
        <Award className="h-3 w-3" />
        <span className="sm:hidden">Tap a photo to see answer sheets · tap ⓘ for marks</span>
        <span className="hidden sm:inline">Tap a winner's photo to view their answer sheets · hover for marks</span>
      </div>

      {/* Mobile bottom-sheet — subject marks for a podium spot */}
      {openStudent && (
        <MarksSheet
          student={openStudent}
          examLabel={data.exam_label}
          onClose={() => setOpenStudentId(null)}
        />
      )}

      {/* Answer sheets viewer — opened by tapping a winner's photo */}
      <StudentAnswerSheetViewer
        open={!!sheetView}
        onClose={() => setSheetView(null)}
        student={sheetView}
        examKey={data.exam_key}
        examLabel={data.exam_label}
      />
    </div>
  );
}


function SubjectToppersCarousel({ toppers, examLabel, onViewSheets }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [celebrate, setCelebrate] = useState(true);
  const n = toppers.length;

  useEffect(() => {
    const t = setTimeout(() => setCelebrate(false), 1400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (paused || n <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 3500);
    return () => clearInterval(t);
  }, [paused, n]);

  const go = (dir) => setIdx((i) => (i + dir + n) % n);
  const t = toppers[idx];
  if (!t) return null;
  const winners = t.toppers && t.toppers.length ? t.toppers : [t];
  const multi = winners.length > 1;

  return (
    <div
      className="relative mt-4 rounded-2xl border border-amber-200/50 dark:border-amber-700/30 bg-white/70 dark:bg-slate-800/60 p-4 overflow-hidden"
      data-testid="subject-toppers-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-600 dark:text-amber-300">
          <Crown className="h-3.5 w-3.5" /> Subject toppers · {examLabel}
        </div>
        {n > 1 && (
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => go(-1)} className="h-6 w-6 rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-200" aria-label="Previous" data-testid="toppers-prev">
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={() => go(1)} className="h-6 w-6 rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-200" aria-label="Next" data-testid="toppers-next">
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      <div
        key={idx}
        className="relative flex items-center gap-3 animate-[slideInRight_0.45s_cubic-bezier(0.16,1,0.3,1)]"
        data-testid={`topper-slide-${idx}`}
      >
        {celebrate && <ConfettiBurst fireKey="open" />}
        {/* Topper avatars — gold crown ring; tap to view answer sheets. */}
        <div className="flex -space-x-1.5 shrink-0 pt-2">
          {winners.slice(0, 4).map((w, wi) => (
            <div key={w.student_id || wi} className="relative">
              {/* Little gold crown sitting on the avatar */}
              <Crown className="absolute left-1/2 -translate-x-1/2 -top-2 h-3.5 w-3.5 text-amber-500 drop-shadow z-20 animate-[crownBob_2.2s_ease-in-out_infinite]" style={{ animationDelay: `${wi * 0.15}s` }} />
              {/* Gold gradient ring wrapper */}
              <span className="block rounded-full p-[2.5px] bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 shadow-[0_0_10px_rgba(245,197,24,0.55)]">
                <button
                  type="button"
                  onClick={() => onViewSheets?.(w)}
                  className={`relative h-12 w-12 rounded-full ring-2 ring-white dark:ring-slate-800 overflow-hidden flex items-center justify-center font-bold text-white transition-transform hover:scale-110 hover:z-10 ${
                    w.is_me ? "bg-gradient-to-br from-amber-400 to-orange-500" : "bg-gradient-to-br from-sky-400 to-indigo-500"
                  }`}
                  style={{ fontFamily: "Outfit" }}
                  title={`${w.name} — tap to view answer sheets`}
                  data-testid={`topper-photo-${idx}-${wi}`}
                >
                  {w.has_photo ? (
                    <img src={`${API_BASE}/profile/photo/${w.student_id}`} alt={w.name} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-sm">{initials(w.name)}</span>
                  )}
                  {w.has_answer_sheets && (
                    <span className="absolute -bottom-0.5 -right-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-800" title="Answer sheets available" data-testid={`topper-answersheet-badge-${idx}-${wi}`}>
                      <FileText className="h-2.5 w-2.5 text-white" />
                    </span>
                  )}
                </button>
              </span>
            </div>
          ))}
          {winners.length > 4 && (
            <span className="h-12 w-12 self-center rounded-full ring-2 ring-white dark:ring-slate-800 bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-600 dark:text-slate-200">+{winners.length - 4}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wider text-slate-400">
            {t.subject}{multi && <span className="ml-1 normal-case tracking-normal text-amber-600 dark:text-amber-300">· {winners.length}-way tie</span>}
          </div>
          <div className="font-semibold text-slate-900 dark:text-slate-100 truncate flex items-center gap-1.5">
            {multi ? winners.map((w) => w.name).join(", ") : winners[0].name}
            {winners.some((w) => w.is_me) && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">You</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-black text-amber-600 dark:text-amber-300" style={{ fontFamily: "Outfit" }}>{winners[0].percentage}%</div>
          <div className="text-[10px] text-slate-400 font-mono">{winners[0].marks}/{winners[0].max_marks}</div>
        </div>
      </div>

      {n > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="toppers-dots">
          {toppers.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-amber-500" : "w-1.5 bg-slate-300 dark:bg-slate-600"}`}
              aria-label={`Go to topper ${i + 1}`}
            />
          ))}
        </div>
      )}
      <style>{`
        @keyframes slideInRight { from { transform: translateX(28px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes crownBob { 0%,100% { transform: translateX(-50%) translateY(0) rotate(-6deg); } 50% { transform: translateX(-50%) translateY(-2px) rotate(6deg); } }
        @keyframes confettiPop {
          0% { transform: translate(0,0) scale(0.6); opacity: 0; }
          15% { opacity: 1; }
          100% { transform: translate(var(--cx), var(--cy)) scale(1); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// A small celebratory confetti burst that fires each time the slide (fireKey)
// changes — makes being a subject topper feel special.
function ConfettiBurst({ fireKey }) {
  const pieces = useMemo(() => {
    const colors = ["#f59e0b", "#f43f5e", "#38bdf8", "#a855f7", "#22c55e", "#facc15"];
    return Array.from({ length: 14 }, (_, i) => {
      const angle = (Math.PI * 2 * i) / 14 + Math.random() * 0.5;
      const dist = 26 + Math.random() * 26;
      return {
        cx: `${Math.cos(angle) * dist}px`,
        cy: `${Math.sin(angle) * dist - 6}px`,
        color: colors[i % colors.length],
        delay: `${Math.random() * 0.08}s`,
        size: 4 + Math.round(Math.random() * 3),
      };
    });
  }, [fireKey]);
  return (
    <div key={fireKey} className="pointer-events-none absolute left-6 top-4 z-30" aria-hidden="true" data-testid="topper-confetti">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="absolute rounded-[1px]"
          style={{
            width: p.size, height: p.size, background: p.color,
            "--cx": p.cx, "--cy": p.cy,
            animation: `confettiPop 0.9s cubic-bezier(0.16,1,0.3,1) ${p.delay} forwards`,
          }}
        />
      ))}
    </div>
  );
}


function YourPerformance({ me, trend, meInTop3, streak }) {
  const delta = trend?.delta;
  const prevRank = trend?.previous_rank;
  const streakCount = streak?.count || 0;

  // Mood + visual palette (stable per render)
  const { mood, IconComp, palette, quotes } = useMemo(() => {
    if (me.rank === 1) {
      return { mood: "champion", IconComp: Trophy, quotes: TOPPER_QUOTES,
        palette: { wrap: "from-amber-400 via-amber-500 to-orange-500", text: "text-white" } };
    }
    if (delta == null || prevRank == null) {
      return { mood: "neutral", IconComp: Sparkles, quotes: NEUTRAL_QUOTES,
        palette: { wrap: "from-sky-500 via-cyan-500 to-teal-500", text: "text-white" } };
    }
    if (delta > 0) return { mood: "up", IconComp: TrendingUp, quotes: UP_QUOTES,
      palette: { wrap: "from-emerald-500 via-teal-500 to-cyan-500", text: "text-white" } };
    if (delta < 0) return { mood: "down", IconComp: TrendingDown, quotes: DOWN_QUOTES,
      palette: { wrap: "from-indigo-500 via-violet-600 to-slate-700", text: "text-white" } };
    return { mood: "same", IconComp: Minus, quotes: SAME_QUOTES,
      palette: { wrap: "from-slate-500 via-slate-600 to-slate-700", text: "text-white" } };
  }, [me.rank, delta, prevRank]);

  // Rotate quotes every 10 seconds
  const [quoteIdx, setQuoteIdx] = useState(0);
  useEffect(() => {
    setQuoteIdx(0);
    if (!quotes || quotes.length <= 1) return undefined;
    const t = setInterval(() => setQuoteIdx((i) => (i + 1) % quotes.length), 10_000);
    return () => clearInterval(t);
  }, [quotes]);
  const quote = quotes[quoteIdx];

  return (
    <div
      className={`relative mt-5 rounded-2xl overflow-hidden bg-gradient-to-br ${palette.wrap} shadow-lg`}
      data-testid={`your-performance-${mood}`}
    >
      {(mood === "up" || mood === "champion") && (
        <>
          <Sparkles className="absolute top-3 right-4 h-5 w-5 text-white/60 animate-pulse" />
          <Sparkles className="absolute bottom-3 left-6 h-4 w-4 text-white/40 animate-pulse [animation-delay:400ms]" />
        </>
      )}

      <div className="relative p-5 sm:p-6 grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-center">
        {/* Compact rank block */}
        <div className="flex items-end gap-2" data-testid="your-rank-big">
          <div className="leading-none">
            <div className={`text-[10px] uppercase tracking-[0.25em] ${palette.text} opacity-80 font-semibold mb-1`}>Your rank</div>
            <div className="flex items-baseline gap-1">
              <span className={`${palette.text} text-3xl sm:text-4xl font-black leading-none`} style={{ fontFamily: "Outfit" }}>#{me.rank}</span>
            </div>
            <div className={`text-[11px] ${palette.text} opacity-90 mt-1 font-medium`}>
              {me.percentage}% · {me.total}<span className="opacity-60">/{me.max_total}</span>
            </div>
            {streakCount > 0 && (
              <div
                className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-bold"
                title={`Your score improved ${streakCount} exam${streakCount === 1 ? "" : "s"} in a row`}
                data-testid="your-streak-chip"
              >
                <Flame className="h-3 w-3 text-orange-200 animate-pulse" />
                {streakCount} exam{streakCount === 1 ? "" : "s"} streak
              </div>
            )}
          </div>

          {delta != null && prevRank != null && (
            <div className={`flex flex-col items-center justify-center min-w-[3rem] sm:min-w-[3.5rem] ${
              delta > 0 ? "bg-white/20" : delta < 0 ? "bg-black/20" : "bg-white/15"
            } rounded-lg px-2 py-1.5 backdrop-blur-sm`}
                 data-testid={`your-trend-${mood}`}>
              <IconComp className={`h-3.5 w-3.5 ${palette.text} ${delta > 0 ? "animate-bounce" : ""}`} />
              <span className={`${palette.text} text-base sm:text-lg font-bold leading-tight`} style={{ fontFamily: "Outfit" }}>
                {delta > 0 ? `↑${delta}` : delta < 0 ? `↓${Math.abs(delta)}` : "="}
              </span>
              <span className={`text-[9px] uppercase tracking-wider ${palette.text} opacity-75 leading-none`}>
                {delta > 0 ? "up" : delta < 0 ? "down" : "same"}
              </span>
            </div>
          )}
        </div>

        {/* Quote — sliding transition every 10 seconds */}
        <div className="min-w-0 overflow-hidden">
          <div className="relative h-[60px] sm:h-[68px]">
            <div
              key={quoteIdx}
              className={`absolute inset-0 ${palette.text} font-bold text-lg sm:text-2xl leading-snug animate-[slideInRight_0.5s_cubic-bezier(0.16,1,0.3,1)]`}
              style={{ fontFamily: "Outfit" }}
              data-testid="your-quote"
            >
              {quote}
            </div>
          </div>
          {prevRank != null ? (
            <div className={`text-[11px] sm:text-xs ${palette.text} opacity-80 mt-0.5`} data-testid="your-prev-rank">
              {delta === 0
                ? `Held at #${prevRank} since ${trend.previous_exam_label}`
                : delta > 0
                  ? `Up from #${prevRank} in ${trend.previous_exam_label}`
                  : `Down from #${prevRank} in ${trend.previous_exam_label}`}
            </div>
          ) : (
            <div className={`text-[11px] sm:text-xs ${palette.text} opacity-80 mt-0.5`}>
              No prior exam yet — make this your benchmark!
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(40px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}


function FullRanking({ ranking, myId, presence = {} }) {
  const [open, setOpen] = useState(false);
  const appearedCount = ranking.filter((r) => r.appeared !== false).length;
  return (
    <div className="mt-4" data-testid="full-ranking">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-white/70 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-700/70 hover:bg-white dark:hover:bg-slate-800 transition-colors"
        data-testid="full-ranking-toggle"
      >
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
          <ListOrdered className="h-4 w-4 text-slate-500" />
          Full class ranking
          <span className="text-[10px] font-normal uppercase tracking-wider text-slate-400 ml-1">
            {appearedCount} appeared · {ranking.length} total
          </span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div
          className="mt-2 rounded-xl bg-white/70 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-700/70 max-h-80 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700"
          data-testid="full-ranking-list"
        >
          {ranking.map((r) => {
            const isMe = r.student_id === myId;
            const isPodium = r.rank != null && r.rank <= 3;
            const notAppeared = r.appeared === false;
            return (
              <div
                key={r.student_id}
                className={`flex items-center gap-3 px-3 py-2 text-sm ${
                  isMe ? "bg-amber-50/70 dark:bg-amber-900/30" : ""
                } ${notAppeared ? "opacity-60" : ""}`}
                data-testid={`full-rank-${r.rank || "na"}-${r.roll_number}`}
              >
                <div className={`h-7 w-7 rounded-full flex items-center justify-center font-bold text-xs ${
                  notAppeared ? "bg-slate-50 dark:bg-slate-700 text-slate-300 dark:text-slate-500"
                  : r.rank === 1 ? "bg-amber-100 text-amber-700"
                  : r.rank === 2 ? "bg-slate-200 text-slate-700"
                  : r.rank === 3 ? "bg-orange-100 text-orange-700"
                  : "bg-slate-50 dark:bg-slate-700 text-slate-500 dark:text-slate-300"
                }`}>{notAppeared ? "—" : r.rank}</div>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium truncate flex items-center gap-1.5 ${isMe ? "text-amber-700 dark:text-amber-200" : "text-slate-800 dark:text-slate-100"}`}>
                    <PresenceDot
                      status={presence[r.student_id]?.status || "offline"}
                      lastSeenAt={presence[r.student_id]?.last_seen_at}
                    />
                    <span className="truncate">{r.name}</span>
                    {isMe && <span className="text-[10px] uppercase tracking-wider ml-1 opacity-70 shrink-0">you</span>}
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono">{r.roll_number}</div>
                </div>
                {notAppeared ? (
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Not appeared</span>
                ) : (
                  <>
                    <div className={`text-sm font-semibold ${
                      r.percentage >= 80 ? "text-emerald-600"
                      : r.percentage >= 60 ? "text-sky-600"
                      : r.percentage >= 40 ? "text-amber-600" : "text-rose-600"
                    }`} style={{ fontFamily: "Outfit" }}>
                      {r.percentage}%
                    </div>
                    {isPodium && (
                      <Medal className={`h-3.5 w-3.5 ${
                        r.rank === 1 ? "text-amber-500" : r.rank === 2 ? "text-slate-400" : "text-orange-500"
                      }`} />
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function MarksSheet({ student, examLabel, onClose }) {
  // Lock body scroll while sheet is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 sm:hidden" data-testid="marks-sheet">
      <button type="button" className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-label="Close"/>
      <div className="absolute bottom-0 left-0 right-0 rounded-t-3xl bg-white dark:bg-slate-900 p-5 pb-7 shadow-2xl animate-[slideUp_0.25s_ease-out]">
        <div className="mx-auto w-10 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 mb-3" />
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">#{student.rank} · {examLabel}</div>
            <div className="font-bold text-slate-900 dark:text-slate-100 text-lg truncate" style={{ fontFamily: "Outfit" }}>{student.name}</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 -mr-2 text-slate-500 hover:text-slate-900 dark:hover:text-slate-200" aria-label="Close" data-testid="marks-sheet-close">
            <X className="h-5 w-5"/>
          </button>
        </div>
        <div className="space-y-1.5">
          {Object.entries(student.marks_by_subject || {}).map(([sub, m]) => (
            <div key={sub} className="flex items-center justify-between text-sm bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2">
              <span className="text-slate-700 dark:text-slate-200">{sub}</span>
              <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">{m.marks}<span className="opacity-50">/{m.max_marks}</span></span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-sm">
          <span className="text-slate-500">Total</span>
          <span className="font-mono font-bold text-slate-900 dark:text-slate-100">{student.total}<span className="opacity-50">/{student.max_total}</span> · {student.percentage}%</span>
        </div>
      </div>
      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
    </div>
  );
}
